import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { fetchConsultPaymentStatus, startConsultPayment } from "../lib/cashfree";
import { consultError } from "../lib/consult";
import { PageLoader } from "../components/LoadingRing";

const MAX_POLLS = 14;

export default function ConsultationPaymentReturn() {
    const [params] = useSearchParams();
    const bookingId = params.get("booking") || sessionStorage.getItem("rhm_pending_consult") || "";
    const navigate = useNavigate();
    const [state, setState] = useState("checking");
    const tries = useRef(0);
    const [retrying, setRetrying] = useState(false);

    const check = useCallback(async () => {
        if (!bookingId) { setState("failed"); return; }
        try {
            const data = await fetchConsultPaymentStatus(bookingId);
            if (data.payment_status === "paid" || data.payment_status === "free") {
                sessionStorage.removeItem("rhm_pending_consult");
                setState("paid");
                setTimeout(() => navigate(`/consultation/intake/${bookingId}`, { replace: true }), 1400);
                return;
            }
            if (data.payment_status === "failed") { setState("failed"); return; }
            tries.current += 1;
            setState(tries.current >= MAX_POLLS ? "pending" : "checking");
            if (tries.current < MAX_POLLS) setTimeout(check, 2500);
        } catch {
            setState("failed");
        }
    }, [bookingId, navigate]);

    useEffect(() => { check(); }, [check]);

    const retry = async () => {
        setRetrying(true);
        try {
            await startConsultPayment(bookingId);
        } catch (e) {
            toast.error(consultError(e));
            setRetrying(false);
        }
    };

    return (
        <div className="pt-32 pb-24 min-h-[70vh]" data-testid="consult-payment-return-page">
            <div className="mx-auto max-w-xl px-6 text-center">
                {state === "checking" && (
                    <div data-testid="consult-payment-checking">
                        <PageLoader />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-6">Confirming your payment</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Hold on a moment — we are waiting for the bank to confirm. Do not close this window.
                        </p>
                    </div>
                )}

                {state === "paid" && (
                    <div data-testid="consult-payment-paid">
                        <CheckCircle2 className="w-12 h-12 text-lime mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Appointment confirmed</h1>
                        <p className="text-zinc-400 mt-3 text-sm">Taking you to your patient intake form…</p>
                    </div>
                )}

                {state === "pending" && (
                    <div data-testid="consult-payment-pending">
                        <RefreshCw className="w-11 h-11 text-gold mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Still confirming</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Your bank is taking longer than usual. Your slot is still held and we will email you the
                            moment it clears — no need to pay again.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center mt-7">
                            <button onClick={() => { tries.current = 0; setState("checking"); check(); }}
                                data-testid="consult-payment-recheck" className="btn-pill btn-secondary">CHECK AGAIN</button>
                            <Link to="/account/consultations" className="btn-pill btn-primary"
                                data-testid="consult-payment-view">MY CONSULTATIONS</Link>
                        </div>
                    </div>
                )}

                {state === "failed" && (
                    <div data-testid="consult-payment-failed">
                        <XCircle className="w-12 h-12 text-red-400 mx-auto" />
                        <h1 className="font-display text-3xl uppercase tracking-tight mt-5">Payment not completed</h1>
                        <p className="text-zinc-400 mt-3 text-sm">
                            Nothing has been charged. Your appointment slot is still held, so you can finish the
                            payment right away.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center mt-7">
                            <button onClick={retry} disabled={retrying || !bookingId}
                                data-testid="consult-payment-retry" className="btn-pill btn-primary disabled:opacity-50">
                                {retrying ? "OPENING…" : "TRY PAYMENT AGAIN"}
                            </button>
                            <Link to="/account/consultations" className="btn-pill btn-secondary"
                                data-testid="consult-payment-back">MY CONSULTATIONS</Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
